import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { TextAreaField, TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import {
  use_draft,
  use_save_basic_info,
  use_saved_basic_info,
} from '@/features/onboarding/hooks';

// Mirrors backend GST_REGEX (15-char GSTIN).
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Prepend https:// when the user typed a bare domain like "example.com". */
function normalize_url(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

type Form = {
  business_name: string;
  owner_name: string;
  email_for_business: string;
  website_url: string;
  description: string;
  gst_number: string;
  business_registration_number: string;
  is_24x7: boolean;
};

const EMPTY: Form = {
  business_name: '',
  owner_name: '',
  email_for_business: '',
  website_url: '',
  description: '',
  gst_number: '',
  business_registration_number: '',
  is_24x7: false,
};

export function BasicInfoPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_saved_basic_info({ enabled: !!draft_q.data });
  const save_m = use_save_basic_info();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  // Client change (9 Oct 2026): only vets describe themselves at registration. Other vendors
  // can still add a description later from their profile.
  const shows_description = (draft_q.data?.module_types ?? []).includes('VET');

  useEffect(() => {
    // Wait for the draft fetch to settle before redirecting — avoids the
    // refresh-bounces-to-/start race.
    if (draft_q.isFetched && !draft_q.data) {
      navigate('/onboarding/start', { replace: true });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        business_name: saved_q.data.business_name,
        owner_name: saved_q.data.owner_name,
        email_for_business: saved_q.data.email_for_business,
        website_url: saved_q.data.website_url ?? '',
        description: saved_q.data.description ?? '',
        gst_number: saved_q.data.gst_number ?? '',
        business_registration_number: saved_q.data.business_registration_number ?? '',
        is_24x7: saved_q.data.is_24x7,
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
    set_field_errors((fe) => {
      if (!(key in fe)) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (form.business_name.trim().length < 2) {
      errors.business_name = 'Business name must be at least 2 characters.';
    }
    if (form.owner_name.trim().length < 2) {
      errors.owner_name = 'Owner name must be at least 2 characters.';
    }
    if (!EMAIL_REGEX.test(form.email_for_business.trim())) {
      errors.email_for_business = 'Enter a valid email, e.g. rahul@example.com.';
    }
    const gst = form.gst_number.trim();
    if (gst !== '' && !GST_REGEX.test(gst)) {
      errors.gst_number = '15-character GSTIN, e.g. 22AAAAA0000A1Z5. Leave blank if unregistered.';
    }
    return errors;
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    const client_errors = validate();
    if (Object.keys(client_errors).length > 0) {
      set_field_errors(client_errors);
      set_error('Please fix the highlighted fields below.');
      return;
    }
    set_field_errors({});

    const website = normalize_url(form.website_url);
    try {
      await save_m.mutateAsync({
        business_name: form.business_name.trim(),
        owner_name: form.owner_name.trim(),
        email_for_business: form.email_for_business.trim(),
        website_url: website || undefined,
        description: shows_description ? form.description.trim() || undefined : undefined,
        gst_number: form.gst_number.trim() || undefined,
        business_registration_number: form.business_registration_number.trim() || undefined,
        is_24x7: form.is_24x7,
      });
      navigate('/onboarding/address');
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save business details', { highlights_fields: true })
          : 'Could not save business details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit} noValidate>
      <StepSection
        step_key="basic-info"
        title="Business details"
        description="The basics about who's running this and how customers reach you."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="basic-info-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <TextField
            label="Business name"
            name="business_name"
            data-testid="field-business-name"
            value={form.business_name}
            onChange={(e) => update('business_name', e.target.value)}
            maxLength={120}
            placeholder="Paws & Treats Café"
            error={field_errors.business_name}
          />
          <TextField
            label="Owner name"
            name="owner_name"
            data-testid="field-owner-name"
            value={form.owner_name}
            onChange={(e) => update('owner_name', e.target.value)}
            maxLength={120}
            placeholder="Rahul Sharma"
            error={field_errors.owner_name}
          />
          <TextField
            label="Business email"
            name="email_for_business"
            type="email"
            data-testid="field-email"
            value={form.email_for_business}
            onChange={(e) => update('email_for_business', e.target.value)}
            maxLength={200}
            placeholder="rahul@example.com"
            error={field_errors.email_for_business}
          />
          <TextField
            label="Website"
            name="website_url"
            data-testid="field-website"
            value={form.website_url}
            onChange={(e) => update('website_url', e.target.value)}
            placeholder="example.com"
            hint="No need to type https:// — we'll add it."
            optional
            error={field_errors.website_url}
          />
          <TextField
            label="GST number"
            name="gst_number"
            data-testid="field-gst"
            value={form.gst_number}
            onChange={(e) => update('gst_number', e.target.value.toUpperCase().slice(0, 15))}
            placeholder="22AAAAA0000A1Z5"
            hint="15-character GSTIN. Skip if not GST-registered."
            optional
            error={field_errors.gst_number}
          />
          <TextField
            label="Business registration number"
            name="business_registration_number"
            data-testid="field-reg-num"
            value={form.business_registration_number}
            onChange={(e) => update('business_registration_number', e.target.value)}
            maxLength={60}
            placeholder="Optional"
            optional
            error={field_errors.business_registration_number}
          />
        </div>
        {shows_description && (
          <TextAreaField
            label="About you"
            name="description"
            data-testid="field-description"
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Your practice, approach and the pets you care for — shown on your vet profile."
            maxLength={2000}
            optional
          />
        )}
        <label
          className="flex items-center gap-2 text-sm"
          data-testid="field-24x7-label"
        >
          <input
            type="checkbox"
            data-testid="field-24x7"
            checked={form.is_24x7}
            onChange={(e) => update('is_24x7', e.target.checked)}
            className="h-4 w-4 rounded border-input accent-primary"
          />
          We operate 24×7
        </label>
      </StepSection>
    </form>
  );
}
